import { updateOneFormula } from "../../api/petFormulaApi";
import { transformDataForApi } from "./utils";
import { CoreBrandsHandlerParams, CoreBrandData } from "../../components/SettingsPage/types";

// 🎯 A row is valid only when it defines at least one core brand
const validateCoreBrands = (rowData: Partial<CoreBrandData>): boolean => {
  const coreBrandFields = ['coreBrand1', 'coreBrand2', 'coreBrand3', 'coreBrand4', 'coreBrand5', 'coreBrand6', 'coreBrand7', 'coreBrand8'];
  
  // A field counts as filled only when it is neither empty nor the "-" placeholder
  return coreBrandFields.some(field => {
    const value = (rowData as any)[field];
    return value !== undefined && value !== null && value !== "" && value !== "-";
  });
};

export const createCoreBrandsHandlers = ({
  isEditingCoreBrands,
  setIsEditingCoreBrands,
  editingCoreBrandsData,
  setEditingCoreBrandsData,
  isEditingOneFormula,
  editingOneFormulaData,
  setEditingOneFormulaData,
  setIsEditingOneFormula,
  apiOneFormulaData,
  displayCoreBrandsData,
  setApiOneFormulaData,
  setDisplayCoreBrandsData,
  messageApi,
  userData,
  selectedWorkspace,
  setEditingCoreBrandsRows,
  setIsPipelineRunning
}: CoreBrandsHandlerParams) => {
  const handleEditAllCoreBrands = async () => {
    if (isEditingCoreBrands) {
      // Save All - persist every edited row
      try {
        messageApi.loading({
          key: "core-brands-update",
          content: "Updating configuration data",
          duration: 0
        });

        const currentPetData = apiOneFormulaData.length > 0 ? apiOneFormulaData : [];
        const currentCoreBrandsData = displayCoreBrandsData.length > 0 ? displayCoreBrandsData : [];

        // 🔄 When OneFormula is under edit as well, its edits are folded into the same save
        let updatedPetData = currentPetData;
        if (isEditingOneFormula && Object.keys(editingOneFormulaData).length > 0) {
          updatedPetData = currentPetData.map(item => ({
            ...item,
            ...editingOneFormulaData[item.key]
          }));
        }

        // Only edited fields are written, so untouched fields keep their original value
        const updatedCoreBrandsData = currentCoreBrandsData.map(item => {
          const editedFields = editingCoreBrandsData[item.key] || {};
          const updatedItem = { ...item };

          Object.keys(editedFields).forEach(field => {
            const editedValue = (editedFields as any)[field];
            // 🎯 A field the user cleared is stored as the "-" placeholder.
            // transformDataForApi skips "-", so the field stays out of the API's core_brands array.
            if (editedValue !== undefined) {
              if (editedValue === "") {
                (updatedItem as any)[field] = "-";
              } else {
                (updatedItem as any)[field] = editedValue;
              }
            }
          });

          return updatedItem;
        });

        // 🎯 Every row must still carry at least one core brand
        const invalidRows = updatedCoreBrandsData.filter(item => !validateCoreBrands(item));
        if (invalidRows.length > 0) {
          messageApi.error({
            key: "core-brands-validation",
            content: "At least 1 Core Brand should be defined in each market.",
            duration: 5
          });
          return;
        }

        // Build the API payload from updatedPetData, so any OneFormula edits are carried along
        const requestBody = transformDataForApi(updatedPetData, updatedCoreBrandsData);

        console.log("requestBody", requestBody);

        await updateOneFormula(requestBody, userData, selectedWorkspace);

        if (setIsPipelineRunning) setIsPipelineRunning(true);

        if (displayCoreBrandsData.length > 0) {
          setDisplayCoreBrandsData(updatedCoreBrandsData);
        }

        // 🔄 OneFormula's local state is cleared too when its edits went out with this save
        if (isEditingOneFormula && Object.keys(editingOneFormulaData).length > 0) {
          if (apiOneFormulaData.length > 0) {
            setApiOneFormulaData(updatedPetData);
          }
          setEditingOneFormulaData({});
          setIsEditingOneFormula(false);
        }

        setEditingCoreBrandsData({});
        setIsEditingCoreBrands(false);

      } catch (error: any) {
        messageApi.error({
          key: "core-brands-update",
          content: `${error.message}`,
          duration: 3
        });
        console.error("[handleEditAllCoreBrands] Error:", error);
      }
    } else {
      // Edit All - enter edit mode
      // Pre-populate all rows with current display values so existing brands are
      // visible and clearable. Without this, untouched brands are silently preserved
      // on save even when the user intended to remove them.
      const initialEditingData: { [key: string]: Partial<CoreBrandData> } = {};
      (displayCoreBrandsData.length > 0 ? displayCoreBrandsData : []).forEach(item => {
        initialEditingData[item.key] = {
          coreBrand1: item.coreBrand1 || "-",
          coreBrand2: item.coreBrand2 || "-",
          coreBrand3: item.coreBrand3 || "-",
          coreBrand4: item.coreBrand4 || "-",
          coreBrand5: item.coreBrand5 || "-",
          coreBrand6: item.coreBrand6 || "-",
          coreBrand7: item.coreBrand7 || "-",
          coreBrand8: item.coreBrand8 || "-",
          competitor: item.competitor || "-",
        };
      });
      setEditingCoreBrandsData(initialEditingData);
      setIsEditingCoreBrands(true);
    }
  };

  const handleCancelCoreBrands = () => {
    // Cancel editing - discard changes and exit edit mode
    setEditingCoreBrandsData({});
    setIsEditingCoreBrands(false);
    if (setEditingCoreBrandsRows) {
      setEditingCoreBrandsRows({});
    }
  };

  const handleCoreBrandInputChange = (key: string, field: keyof CoreBrandData, value: any) => {
    setEditingCoreBrandsData(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  // When a brand is cleared, compact remaining brands left so there are no gaps
  const handleCoreBrandClearAndCompact = (key: string, clearedIndex: number) => {
    setEditingCoreBrandsData((prev: any) => {
      const currentData = (displayCoreBrandsData.length > 0 ? displayCoreBrandsData : [])
        .find((item: any) => item.key === key);
      if (!currentData) return prev;

      // Collect all 8 brand values (editing state takes priority, fall back to display data)
      const brands: string[] = Array.from({ length: 8 }, (_, j) => {
        const fName = `coreBrand${j + 1}`;
        const editingVal = prev[key]?.[fName];
        const val = editingVal !== undefined ? editingVal : ((currentData as any)[fName] || "");
        return val === "-" ? "" : (val as string);
      });

      // Remove the cleared slot
      brands[clearedIndex] = "";

      // Compact: filter non-empty, pad to 8
      const compacted = brands.filter((v: string) => v !== "");
      while (compacted.length < 8) compacted.push("");

      const newRowData = { ...(prev[key] || {}) };
      for (let j = 0; j < 8; j++) {
        (newRowData as any)[`coreBrand${j + 1}`] = compacted[j];
      }

      return { ...prev, [key]: newRowData };
    });
  };

  // 🔧 Individual row editing functions
  const handleEditRow = (key: string) => {
    const currentData = displayCoreBrandsData.length > 0 ? displayCoreBrandsData : [];
    const rowData = currentData.find(item => item.key === key);

    if (rowData && setEditingCoreBrandsRows) {
      // Initialize editing data for this row
      setEditingCoreBrandsData(prev => ({
        ...prev,
        [key]: {
          coreBrand1: rowData.coreBrand1,
          coreBrand2: rowData.coreBrand2,
          coreBrand3: rowData.coreBrand3,
          coreBrand4: rowData.coreBrand4,
          coreBrand5: rowData.coreBrand5,
          coreBrand6: rowData.coreBrand6,
          coreBrand7: rowData.coreBrand7,
          coreBrand8: rowData.coreBrand8,
          competitor: rowData.competitor,
        }
      }));

      // Set this row as editing
      setEditingCoreBrandsRows(prev => ({
        ...prev,
        [key]: true
      }));
    }
  };

  const handleSaveRow = async (key: string) => {
    try {
      const currentData = displayCoreBrandsData.length > 0 ? displayCoreBrandsData : [];
      const editedRowData = editingCoreBrandsData[key];

      if (!editedRowData) {
        messageApi.error({
          key: `core-brands-row-update-${key}`,
          content: "No changes to save",
          duration: 3
        });
        return;
      }

      // Find the row to update
      const rowToUpdate = currentData.find(item => item.key === key);
      if (!rowToUpdate) {
        messageApi.error({
          key: `core-brands-row-update-${key}`,
          content: "Row not found",
          duration: 3
        });
        return;
      }

      // Create updated row data - only update fields that were actually edited
      const updatedRowData = { ...rowToUpdate };
      
      // Only update fields that exist in editedRowData and are not undefined
      Object.keys(editedRowData).forEach(field => {
        const editedValue = (editedRowData as any)[field];
        // 🎯 A field the user cleared is stored as the "-" placeholder.
        // transformDataForApi skips "-", so the field stays out of the API's core_brands array.
        if (editedValue !== undefined) {
          if (editedValue === "") {
            (updatedRowData as any)[field] = "-";
          } else {
            (updatedRowData as any)[field] = editedValue;
          }
        }
      });

      // 🎯 This row must still carry at least one core brand
      if (!validateCoreBrands(updatedRowData)) {
        messageApi.error({
          key: `core-brands-row-update-${key}`,
          content: "At least 1 Core Brand should be defined in each market.",
          duration: 5
        });
        return;
      }

      // Show loading message
      messageApi.loading({
        key: `core-brands-row-update-${key}`,
        content: "Saving row data...",
        duration: 0
      });

      // 🚀 Call API to save the complete data (following the same pattern as handleEditAllCoreBrands)
      const currentPetData = apiOneFormulaData.length > 0 ? apiOneFormulaData : [];
      const currentCoreBrandsData = displayCoreBrandsData.length > 0 ? displayCoreBrandsData : [];

      // Apply the single row edit to the complete dataset
      const updatedCoreBrandsDataForApi = currentCoreBrandsData.map(item =>
        item.key === key ? updatedRowData : item
      );

      // 🔄 If OneFormula is also in editing state, apply its edited data
      let updatedPetData = currentPetData;
      if (isEditingOneFormula && Object.keys(editingOneFormulaData).length > 0) {
        updatedPetData = currentPetData.map(item => ({
          ...item,
          ...editingOneFormulaData[item.key]
        }));
      }

      // Transform data for API (same as handleEditAllCoreBrands)
      const requestBody = transformDataForApi(updatedPetData, updatedCoreBrandsDataForApi);

      // Call API to update (same as handleEditAllCoreBrands)
      const response = await updateOneFormula(requestBody, userData, selectedWorkspace);

      if (setIsPipelineRunning) setIsPipelineRunning(true);

      // 🔄 Update local data after successful API call (same pattern as handleEditAllCoreBrands)
      if (displayCoreBrandsData.length > 0) {
        setDisplayCoreBrandsData(updatedCoreBrandsDataForApi);
      }

      // 🔄 If OneFormula also has editing data, update the underlying data
      // but do NOT clear the editing state — user may still be editing OneFormula rows
      if (isEditingOneFormula && Object.keys(editingOneFormulaData).length > 0) {
        if (apiOneFormulaData.length > 0) {
          setApiOneFormulaData(updatedPetData);
        }
      }

      // Clear editing state for this row
      if (setEditingCoreBrandsRows) {
        setEditingCoreBrandsRows((prev) => {
          const newState = { ...prev };
          delete newState[key];
          return newState;
        });
      }

      setEditingCoreBrandsData((prev) => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });

      console.log("Core Brands row saved via API:", updatedRowData, "API Response:", response);

    } catch (error: any) {
      // 🎯 Same error handling logic as handleEditAllCoreBrands
      messageApi.error({
        key: `core-brands-row-update-${key}`,
        content: `Failed to save row: ${error.message}`,
        duration: 3
      });
      console.error("[handleSaveRow] Error:", error);
    }
  };

  return {
    handleEditAllCoreBrands,
    handleCancelCoreBrands,
    handleCoreBrandInputChange,
    handleCoreBrandClearAndCompact,
    handleEditRow,
    handleSaveRow
  };
};